/*! Copyright (c) Safe As Milk. All rights reserved. */
import { animate, glide, inView, stagger } from "motion";

import { ANIMATION_LOAD, ANIMATION_INTERVAL, ANIMATION_INTERVAL_STYLE, ANIMATION_DURATION, ANIMATION_DISPLACEMENT } from "store-data";

import { debounce } from "utils";

/* Kunstiek 27-10-2026: animaties uit op productpagina's als dat zo is ingesteld (Thema-instellingen > Kunstiek, snippets/kunstiek-stijl.liquid) */
const KUNSTIEK_UIT = !!(window.KunstiekAnimatie && window.KunstiekAnimatie.uit);

const ANIMATION_ON = ANIMATION_LOAD && !KUNSTIEK_UIT;

/* Kunstiek 27-10-2026: eigen blokken laten meedoen met de animatie; bannerfoto bovenaan collecties niet */
const KUNSTIEK_ANIMATE = ".artist-other-artworks-section, .recently-viewed-products .container, .kc-onder, .social-media-follow, .artist-info__text, .kc__wrap, .kk__kop";

/* Kolommen (Kunst verkopen, Over Kunstiek): een voor een binnenkomen */
const KUNSTIEK_ANIMATE_SEQUENCE = ".kk__raster";

/* Kunstiek 27-10-2026: kunstenaarslijst (homepage) en contactblok: onderdelen een voor een binnenkomen in plaats van als een blok */
const KUNSTIEK_ANIMATE_PARTS = [ {
    container: ".artist-list-container",
    parts: ".artist-list-header, .artist-list-item, .artist-list-cta",
    max: 12
}, {
    container: ".contact-buttons-wrapper",
    parts: ".contact-buttons__heading, .contact-button, .contact-buttons__social",
    max: 12
} ];

/* Kunstiek 27-10-2026: een blok begint pas als het zo ver in beeld is (procent van de schermhoogte, vanaf de onderkant). Thema-instellingen > Kunstiek */
const KUNSTIEK_START = window.KunstiekAnimatie && typeof window.KunstiekAnimatie.start === "number" ? Math.min(Math.max(window.KunstiekAnimatie.start, 0), 40) : 0;

const KUNSTIEK_MARGIN = `0px 0px -${KUNSTIEK_START}% 0px`;

const KUNSTIEK_NO_ANIMATE = ".collection__header-media";

function kunstiekPrepareAnimations(rootElement) {
    rootElement.querySelectorAll(KUNSTIEK_NO_ANIMATE).forEach((element => {
        element.classList.remove("js-animate-single");
    }));
    rootElement.querySelectorAll(KUNSTIEK_ANIMATE).forEach((element => {
        if (!element.closest(".js-animate-single, .js-animate-sequence")) element.classList.add("js-animate-single");
    }));
    rootElement.querySelectorAll(KUNSTIEK_ANIMATE_SEQUENCE).forEach((element => {
        if (element.closest(".js-animate-single, .js-animate-sequence")) return;
        element.classList.add("js-animate-sequence");
        if (!element.dataset.animationInterval) element.dataset.animationInterval = "0.15";
    }));
    KUNSTIEK_ANIMATE_PARTS.forEach((group => {
        rootElement.querySelectorAll(group.container).forEach((element => {
            if (element.closest(".js-animate-single, .js-animate-sequence")) return;
            const count = element.querySelectorAll(group.parts).length;
            if (count > 0 && count <= group.max) {
                element.classList.add("js-animate-sequence");
                element.dataset.animationSelector = group.parts;
                if (!element.dataset.animationInterval) element.dataset.animationInterval = "0.12";
            } else {
                element.classList.add("js-animate-single");
            }
        }));
    }));
}

/* Kunstiek 04-11-2026: staat het blok (een deel ervan) al in beeld zolang de pagina nog laadt? Dan niet animeren.
   Alleen bij het openen van de pagina; blokken die later via de editor of het filter worden toegevoegd, animeren gewoon. */
let kunstiekPaginaGeladen = false;

window.addEventListener("load", (() => {
    setTimeout((() => {
        kunstiekPaginaGeladen = true;
    }), 1500);
}));

function kunstiekAlInBeeld(element) {
    if (kunstiekPaginaGeladen) return false;
    const rect = element.getBoundingClientRect();
    const height = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < height && rect.bottom > 0;
}

// Kunstiek 29-10-2026: "duration" weggehaald, zodat secties met een eigen animatie-instelling (zoals de Galerij "Uitgelicht")
// dezelfde snelheid volgen als de rest van de site (Thema-instellingen > Kunstiek).
const ALLOWED_SETTINGS_KEYS = [ "amount", "delay", "displacement", "interval", "scale", "selector" ];

class Animations {
    #boundUpdateScrollDelta;
    #displacement;
    #duration;
    #interval;
    #reducedMotionMediaQuery;
    #previousScrollPosition;
    #scrollDelta=0;
    static #instantiated=false;
    constructor(displacement = 10, duration = .5, interval = ANIMATION_INTERVAL) {
        if (Animations.#instantiated) throw new Error("Animations instance already exists");
        Animations.#instantiated = true;
        if (!ANIMATION_ON) {
            if (KUNSTIEK_UIT) document.body.classList.remove("js-theme-loading");
            return;
        }
        this.#reducedMotionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        this.#displacement = ANIMATION_INTERVAL_STYLE !== "fade_in" ? displacement : 0;
        this.#duration = duration;
        this.#interval = interval;
        this.#previousScrollPosition = window.scrollY || document.documentElement.scrollTop;
        this.#boundUpdateScrollDelta = this.#updateScrollDelta.bind(this);
        this.setup();
        Animations.#revealOnPageLoad();
        window.addEventListener("scroll", debounce(this.#boundUpdateScrollDelta, 16));
        window.addEventListener("scrollend", this.#boundUpdateScrollDelta);
    }
    setupSingle(element) {
        if (!ANIMATION_ON) return;
        if (!element) throw new Error("Element must be provided for animation");
        let amount = element.dataset.animationAmount ? Number(element.dataset.animationAmount) : 0;
        if (amount < 0) {
            amount = 0;
        } else if (amount >= 1) {
            amount = .99;
        }
        const settings = {
            delay: element.dataset.animationDelay ? parseFloat(element.dataset.animationDelay) : 0,
            displacement: (element.dataset.animationDisplacement ? Number(element.dataset.animationDisplacement) : this.#displacement) * (ANIMATION_INTERVAL_STYLE === "fade_down" ? -1 : 1),
            duration: element.dataset.animationDuration ? parseFloat(element.dataset.animationDuration) : this.#duration,
            scale: element.dataset.animationScale ? parseFloat(element.dataset.animationScale) : 1
        };
        const transform = `matrix(${settings.scale}, 0, 0, ${settings.scale}, 0, ${settings.displacement})`;
        /* Kunstiek 04-11-2026: blok dat bij het openen van de pagina al in beeld staat, meteen tonen (geen infaden) */
        if (kunstiekAlInBeeld(element)) return;
        element.style.setProperty("opacity", 0);
        element.style.setProperty("transform", transform);
        inView(element, (info => {
            const {target: target} = info;
            const animation = animate(target, {
                opacity: 1,
                transform: `matrix(1, 0, 0, 1, 0, 0)`
            }, {
                autoplay: false,
                delay: settings.delay,
                duration: this.#reducedMotionMediaQuery.matches ? 0 : settings.duration,
                easing: glide()
            });
            animation.play();
            animation.finished.then((() => {
                target.classList.add("js-animation-done");
                target.style.removeProperty("opacity");
                target.style.removeProperty("transform");
            }));
            element.animations = [ {
                animation: animation,
                transformSettings: {
                    elements: [ target ],
                    transform: transform
                }
            } ];
            return () => {
                if (element.dataset.animationReplay) {
                    animation.cancel();
                    target.classList.remove("js-animation-done");
                    target.style.setProperty("opacity", 0);
                    target.style.setProperty("transform", transform);
                }
            };
        }), {
            amount: amount,
            margin: KUNSTIEK_MARGIN
        });
    }
    setupSequence(element) {
        if (!ANIMATION_ON) return;
        if (!element) throw new Error("Element must be provided for animation");
        let amount = element.dataset.animationAmount ? Number(element.dataset.animationAmount) : 0;
        if (amount < 0) {
            amount = 0;
        } else if (amount >= 1) {
            amount = .99;
        }
        const defaultSettings = {
            delay: element.dataset.animationDelay ? parseFloat(element.dataset.animationDelay) : 0,
            displacement: (element.dataset.animationDisplacement ? Number(element.dataset.animationDisplacement) : this.#displacement) * (ANIMATION_INTERVAL_STYLE === "fade_down" ? -1 : 1),
            duration: element.dataset.animationDuration ? parseFloat(element.dataset.animationDuration) : this.#duration,
            interval: element.dataset.animationInterval ? parseFloat(element.dataset.animationInterval) : this.#interval,
            selector: element.dataset.animationSelector,
            scale: element.dataset.animationScale ? parseFloat(element.dataset.animationScale) : 1
        };
        const settings = element.dataset.animation ? JSON.parse(element.dataset.animation).reduce(((finalSettings, settingsGroup) => {
            const filteredSettingsGroup = Object.keys(settingsGroup).filter((key => ALLOWED_SETTINGS_KEYS.includes(key))).reduce(((group, key) => {
                group[key] = settingsGroup[key];
                return group;
            }), {
                ...defaultSettings
            });
            return [ ...finalSettings, filteredSettingsGroup ];
        }), []) : [ defaultSettings ];
        /* Kunstiek 04-11-2026: blok dat bij het openen van de pagina al in beeld staat, meteen tonen (geen infaden) */
        if (kunstiekAlInBeeld(element)) return;
        const elementsTransformsSettings = [];
        settings.forEach((settingsGroup => {
            const elements = settingsGroup.selector ? Array.from(element.querySelectorAll(settingsGroup.selector)) : Array.from(element.children);
            const transform = `matrix(${settingsGroup.scale}, 0, 0, ${settingsGroup.scale}, 0, ${settingsGroup.displacement})`;
            elements.forEach((el => {
                el.style.setProperty("opacity", 0);
            }));
            elements.forEach((el => {
                el.style.setProperty("transform", transform);
            }));
            elementsTransformsSettings.push({
                elements: elements,
                transform: transform
            });
        }));
        inView(element, (info => {
            const {target: target} = info;
            const animations = [];
            settings.forEach(((settingsGroup, settingsIndex) => {
                const elements = settingsGroup.selector ? Array.from(target.querySelectorAll(settingsGroup.selector)) : Array.from(target.children);
                const elementsInViewportIndexes = elements.reduce(((finalElements, el, index) => [ ...finalElements, ...Animations.#isElementInViewport(el) ? [ index ] : [] ]), []);
                const elementToStaggerFrom = this.#scrollDelta < 0 ? elementsInViewportIndexes[elementsInViewportIndexes.length - 1] : elementsInViewportIndexes[0];
                const animation = animate(elements, {
                    opacity: 1,
                    transform: `matrix(1, 0, 0, 1, 0, 0)`
                }, {
                    delay: settingsGroup.interval ? stagger(settingsGroup.interval, {
                        easing: "linear",
                        from: elementToStaggerFrom,
                        start: settingsGroup.delay
                    }) : settingsGroup.delay,
                    duration: this.#reducedMotionMediaQuery.matches ? 0 : settingsGroup.duration,
                    easing: glide()
                });
                animation.finished.then((() => {
                    elements.forEach((el => {
                        el.classList.add("js-animation-done");
                        el.style.removeProperty("opacity");
                        el.style.removeProperty("transform");
                    }));
                }));
                animations.push({
                    animation: animation,
                    transformSettings: elementsTransformsSettings[settingsIndex]
                });
            }));
            element.animations = animations;
            return () => {
                if (element.dataset.animationReplay) {
                    element.classList.remove("js-animation-done");
                    animations.forEach((animation => {
                        animation.cancel();
                    }));
                    elementsTransformsSettings.forEach((({elements: elements, transform: transform}) => {
                        elements.forEach((el => {
                            el.classList.remove("js-animation-done");
                            el.style.setProperty("opacity", 0);
                            el.style.setProperty("transform", transform);
                        }));
                    }));
                }
            };
        }), {
            amount: amount,
            margin: KUNSTIEK_MARGIN
        });
    }
    setup(rootElement = document) {
        if (!ANIMATION_ON) return;
        if (!rootElement) throw new Error("Element must be provided for animation");
        kunstiekPrepareAnimations(rootElement);
        rootElement.querySelectorAll(".js-animate-single").forEach((element => {
            this.setupSingle(element);
        }));
        rootElement.querySelectorAll(".js-animate-sequence").forEach((element => {
            this.setupSequence(element);
        }));
    }
    static #revealOnPageLoad() {
        document.body.classList.add("js-theme-loaded");
        document.body.classList.remove("js-theme-loading");
        setTimeout((() => {
            document.body.classList.remove("js-theme-loaded");
        }), 300);
    }
    #updateScrollDelta() {
        const st = window.scrollY || document.documentElement.scrollTop;
        if (st > this.#previousScrollPosition) {
            this.#scrollDelta = 1;
        } else if (st < this.#previousScrollPosition) {
            this.#scrollDelta = -1;
        } else {
            this.#scrollDelta = 0;
        }
        this.#previousScrollPosition = st <= 0 ? 0 : st;
    }
    static #isElementInViewport(element, strict = false, options = {}) {
        const rect = element.getBoundingClientRect();
        const height = window.innerHeight || document.documentElement.clientHeight;
        const width = window.innerWidth || document.documentElement.clientWidth;
        const offset = {
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            ...options
        };
        if (strict) {
            return rect.top >= -offset.top && rect.left >= -offset.left && rect.bottom <= height + offset.bottom && rect.right <= width + offset.right;
        }
        return rect.right >= -offset.left && rect.bottom >= -offset.top && rect.left <= width + offset.right && rect.top <= height + offset.bottom;
    }
    static resetAnimations(element) {
        if (!ANIMATION_ON) return;
        if (!element || !element.animations || element.dataset.animationReplay) return;
        const {animations: animations} = element;
        element.classList.remove("js-animation-done");
        animations.forEach((({animation: animation, transformSettings: {elements: elements, transform: transform}}) => {
            animation.cancel();
            elements.forEach((el => {
                el.classList.remove("js-animation-done");
                el.style.setProperty("opacity", 0);
                el.style.setProperty("transform", transform);
            }));
        }));
    }
    static setRevealedState(element) {
        if (!ANIMATION_ON) return;
        if (!element) return;
        if (element.classList.contains("js-animate-single")) {
            element.style.setProperty("opacity", 1);
            element.style.setProperty("transform", "matrix(1, 0, 0, 1, 0, 0)");
        } else if (element.classList.contains("js-animate-sequence")) {
            let selector = "";
            if (element.dataset.animationSelector) {
                selector = element.dataset.animationSelector;
            }
            if (element.dataset.animation) {
                const parsedSettings = JSON.parse(element.dataset.animation);
                if (parsedSettings.selector) {
                    selector = parsedSettings.selector;
                }
            }
            const elements = selector ? Array.from(element.querySelectorAll(selector)) : Array.from(element.children);
            elements.forEach((el => {
                el.style.setProperty("opacity", 1);
                el.style.setProperty("transform", "matrix(1, 0, 0, 1, 0, 0)");
            }));
        }
    }
}

/* Kunstiek 27-10-2026: duur en afstand instelbaar via Thema-instellingen > Kunstiek.
   Eerst uit de pagina zelf (snippets/kunstiek-stijl.liquid), anders uit store-data.js.liquid. */
const kunstiekAnimatie = window.KunstiekAnimatie || {};

const PageAnimations = new Animations(typeof kunstiekAnimatie.afstand === "number" ? kunstiekAnimatie.afstand : ANIMATION_DISPLACEMENT, typeof kunstiekAnimatie.duur === "number" && kunstiekAnimatie.duur > 0 ? kunstiekAnimatie.duur / 1000 : ANIMATION_DURATION);

export default PageAnimations;

export { Animations };
//# sourceMappingURL=animations.js.map
